import { useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, App as AntApp } from 'antd';
import enUS from 'antd/locale/en_US';
import frFR from 'antd/locale/fr_FR';
import arEG from 'antd/locale/ar_EG';

type AntdLocale = typeof enUS;
import { MotionConfig } from 'framer-motion';
import { RouterProvider } from 'react-router';
import { router } from './router';
import { createTheme } from './lib/theme';
import { OnboardingProvider } from './hooks/useOnboarding';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { AuthProvider } from './contexts/AuthContext';
import { TooltipProvider } from './components/ui/tooltip';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LocaleProvider, useLocale } from './contexts/LocaleContext';
import { DateRangeProvider } from './contexts/DateRangeContext';
import type { AppLocale } from './i18n';
import './i18n';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const ANTD_LOCALES: Record<AppLocale, AntdLocale> = {
  en: enUS,
  fr: frFR,
  ar: arEG,
};

function ThemedApp() {
  const { mode } = useTheme();
  const { locale, direction } = useLocale();

  const theme = useMemo(() => createTheme({ mode }), [mode]);

  return (
    /*
     * `reducedMotion="user"` makes every descendant `motion` component drop
     * transform and layout animations when the OS asks for reduced motion,
     * while leaving opacity and colour alone.
     *
     * This is the Framer half of the contract `styles/tokens.css:181-190`
     * already states for CSS. It has to live here rather than at the call
     * sites: `whileHover={{ scale }}` is animated by JS writing an inline
     * transform, so the stylesheet's `prefers-reduced-motion` block cannot
     * reach it — 36 such props across 10 files were animating regardless of
     * the setting. Several sit inside `.map()` bodies where a per-site
     * `useReducedMotion()` call would break the rules of hooks.
     *
     * Guarded by components/ui/motion/__tests__/hoverReducedMotion.guard.test.ts.
     */
    <MotionConfig reducedMotion="user">
    <ConfigProvider theme={theme} locale={ANTD_LOCALES[locale]} direction={direction}>
      <AntApp>
        <TooltipProvider>
          <AuthProvider>
            <WorkspaceProvider>
              <OnboardingProvider>
                <DateRangeProvider>
                  <RouterProvider router={router} />
                </DateRangeProvider>
              </OnboardingProvider>
            </WorkspaceProvider>
          </AuthProvider>
        </TooltipProvider>
      </AntApp>
    </ConfigProvider>
    </MotionConfig>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LocaleProvider>
          <ThemedApp />
        </LocaleProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
